import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { StationReportReason, StationReportRow, StreamFailureStatus } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { useToast } from "../lib/toast.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { Button, Card, Empty, ErrorState } from "../components/Ui.tsx";

/**
 * Raadiocast kullanıcılarının oynatıcıdaki "Bildir" düğmesiyle gönderdiği
 * istasyon şikayetleri. Satır başına istasyon + sebep; sayı kabaca kaç
 * kullanıcının şikayet ettiği (uygulama aynı şikayeti cihaz başına günde bir
 * kez gönderir). Düzeltilince "Çözüldü" ile kapatılır; yeniden şikayet gelirse
 * kendiliğinden açılır.
 */
const APP_ID = "raadiocast";

const STATUS_LABEL: Record<StreamFailureStatus, string> = {
  open: "Açık",
  resolved: "Çözüldü",
  all: "Tümü",
};

const REASON_LABEL: Record<StationReportReason, string> = {
  not_working: "Çalışmıyor",
  wrong_station: "Yanlış radyo",
  logo: "Logo yok / yanlış",
  wrong_info: "Bilgi yanlış",
  poor_quality: "Ses sorunu",
  other: "Diğer",
};

const rowKey = (row: StationReportRow): string => `${row.stationId}|${row.reason}`;

export function StationReportsPage(): ReactNode {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<StreamFailureStatus>("open");
  const [reason, setReason] = useState<StationReportReason | "all">("all");
  const [search, setSearch] = useState("");

  const reports = useQuery({
    queryKey: ["station-reports", status],
    queryFn: () => client.stationReports(APP_ID, status),
  });

  const resolve = useMutation({
    mutationFn: ({ row, resolved }: { row: StationReportRow; resolved: boolean }) =>
      client.resolveStationReport(APP_ID, row.stationId, row.reason, resolved),
    onSuccess: (_res, { resolved }) => {
      void queryClient.invalidateQueries({ queryKey: ["station-reports"] });
      toast({ tone: "ok", title: resolved ? "Çözüldü olarak işaretlendi" : "Yeniden açıldı" });
    },
    onError: (err, { resolved }) =>
      toast({
        tone: "danger",
        title: resolved ? "İşaretlenemedi" : "Yeniden açılamadı",
        body: humanizeError(err),
      }),
  });

  const needle = search.trim().toLowerCase();
  const rows = (reports.data ?? []).filter(
    (row) =>
      (reason === "all" || row.reason === reason) &&
      (!needle ||
        (row.stationName ?? "").toLowerCase().includes(needle) ||
        row.stationId.toLowerCase().includes(needle) ||
        (row.lastUrl ?? "").toLowerCase().includes(needle)),
  );

  return (
    <>
      <PageHeader
        kicker="raadiocast"
        title="İstasyon şikayetleri"
        actions={
          <div className="flex flex-wrap gap-2">
            <select
              className="input !w-auto"
              aria-label="Sebep"
              value={reason}
              onChange={(e) => setReason(e.target.value as StationReportReason | "all")}
            >
              <option value="all">Tüm sebepler</option>
              {(Object.keys(REASON_LABEL) as StationReportReason[]).map((key) => (
                <option key={key} value={key}>
                  {REASON_LABEL[key]}
                </option>
              ))}
            </select>
            <select
              className="input !w-auto"
              aria-label="Durum"
              value={status}
              onChange={(e) => setStatus(e.target.value as StreamFailureStatus)}
            >
              {(Object.keys(STATUS_LABEL) as StreamFailureStatus[]).map((key) => (
                <option key={key} value={key}>
                  {STATUS_LABEL[key]}
                </option>
              ))}
            </select>
          </div>
        }
      />

      <PageBody>
        <Card
          title={reports.data ? `${rows.length} şikayet` : "Şikayetler"}
          hint="cihaz başına günde bir şikayet · 120 gün sessiz kalan silinir"
          action={
            <input
              className="input w-full sm:!w-56"
              aria-label="İstasyon ya da adres ara"
              placeholder="istasyon ya da adres ara"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          }
          flush
        >
          {reports.isError ? (
            <ErrorState error={reports.error} onRetry={() => void reports.refetch()} />
          ) : reports.isLoading ? (
            <Empty>Yükleniyor…</Empty>
          ) : rows.length ? (
            rows.map((row) => (
              <ReportRow
                key={rowKey(row)}
                row={row}
                pending={
                  resolve.isPending && !!resolve.variables && rowKey(resolve.variables.row) === rowKey(row)
                }
                disabled={resolve.isPending}
                onToggle={() => resolve.mutate({ row, resolved: !row.resolvedAt })}
              />
            ))
          ) : needle ? (
            <Empty>“{search.trim()}” ile eşleşen şikayet yok</Empty>
          ) : (
            <Empty>
              {status === "open"
                ? "Açık şikayet yok"
                : status === "resolved"
                  ? "Çözüldü olarak işaretlenmiş şikayet yok"
                  : "Gönderilmiş şikayet yok"}
            </Empty>
          )}
        </Card>
      </PageBody>
    </>
  );
}

function ReportRow({
  row,
  pending,
  disabled,
  onToggle,
}: {
  row: StationReportRow;
  pending: boolean;
  disabled: boolean;
  onToggle: () => void;
}): ReactNode {
  const name = row.stationName || row.stationId;
  const stationsLink = `/stations?${new URLSearchParams({
    country: row.country || "TR",
    q: row.stationName || row.stationId,
  }).toString()}`;
  return (
    <div className="row flex-wrap items-start gap-3">
      <span
        className="mono w-10 shrink-0 text-right text-md"
        style={{ color: row.resolvedAt ? "var(--color-neutral-600)" : "var(--color-danger)" }}
        title={`${row.reportCount} şikayet`}
      >
        {row.reportCount}
      </span>
      {row.lastLogo ? (
        <img
          src={row.lastLogo}
          alt=""
          aria-hidden
          className="size-9 shrink-0 rounded-[6px] bg-neutral-900 object-contain"
          onError={(e) => {
            e.currentTarget.style.visibility = "hidden";
          }}
        />
      ) : (
        <span className="size-9 shrink-0 rounded-[6px] border border-dashed border-neutral-700" aria-hidden />
      )}
      <div className="grid min-w-0 flex-1 basis-[220px] gap-1">
        <span className="flex flex-wrap items-center gap-2 text-neutral-100">
          <Link to={stationsLink} className="hover:underline" title="İstasyonlar sayfasında aç">
            {name}
          </Link>
          {row.country && <span className="tag tag-neutral">{row.country}</span>}
          <span className="tag tag-accent">{REASON_LABEL[row.reason] ?? row.reason}</span>
        </span>
        {row.lastUrl && (
          <a
            href={row.lastUrl}
            target="_blank"
            rel="noreferrer"
            className="mono truncate text-xs text-accent-300 hover:underline"
          >
            {row.lastUrl}
          </a>
        )}
        {row.notes.length > 0 && (
          <ul className="grid gap-1">
            {[...row.notes].reverse().map((note) => (
              <li key={note.at} className="text-sm text-neutral-300">
                “{note.text}”
                <span className="text-2xs text-neutral-600">
                  {" "}
                  · {new Date(note.at).toLocaleDateString("tr-TR")}
                  {note.locale ? ` · ${note.locale}` : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
        <span className="text-2xs text-neutral-600">
          son {new Date(row.lastSeen).toLocaleString("tr-TR")} · ilk{" "}
          {new Date(row.firstSeen).toLocaleDateString("tr-TR")}
          {row.lastAppVersion ? ` · v${row.lastAppVersion}` : ""}
          {row.resolvedAt ? ` · çözüldü ${new Date(row.resolvedAt).toLocaleDateString("tr-TR")}` : ""}
        </span>
      </div>
      <Button variant="ghost" className="!ml-auto" loading={pending} disabled={disabled} onClick={onToggle}>
        {row.resolvedAt ? "Yeniden aç" : "Çözüldü"}
      </Button>
    </div>
  );
}
